import { useState } from 'react';
import { motion } from 'motion/react';
import { Ruler, Plus, Trash2 } from 'lucide-react';
import { useApp } from '@/store';
import { SectionId } from '@/lib/section-permissions';
import { useSectionRights } from '@/lib/use-section-rights';
import { useT } from '@/lib/i18n';
import { sortOptions } from '@/lib/hose-options';
import { useConfirm } from '@/components/ConfirmDialog';
import type { I18nKey } from '@/lib/i18n';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';

function OptionList({
  titleKey,
  items,
  placeholder,
  onAdd,
  onRemove,
  canAdd,
  canRemove,
}: {
  titleKey: I18nKey;
  items: string[];
  placeholder: string;
  onAdd: (v: string) => void;
  onRemove: (v: string) => void;
  // Schlauchoptionen rights (#95): Erstellen adds, Löschen removes.
  canAdd: boolean;
  canRemove: boolean;
}) {
  const t = useT();
  const [draft, setDraft] = useState('');
  const sorted = [...items].sort((a, b) => a.localeCompare(b));

  const handleAdd = () => {
    const v = draft.trim();
    if (!v || items.includes(v)) return;
    onAdd(v);
    setDraft('');
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm flex items-center justify-between">
          <span>{t(titleKey)}</span>
          <Badge variant="outline">{items.length}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {canAdd && (
          <div className="flex gap-2">
            <Input
              value={draft}
              onChange={e => setDraft(e.target.value)}
              placeholder={placeholder}
              onKeyDown={e => e.key === 'Enter' && handleAdd()}
            />
            <Button
              size="sm"
              onClick={handleAdd}
              disabled={!draft.trim()}
              className="shrink-0"
            >
              <Plus className="h-4 w-4" />
              {t('hoseOptions.addValue')}
            </Button>
          </div>
        )}
        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">
            {t('hoseOptions.empty')}
          </p>
        ) : (
          <div className="space-y-1">
            {sorted.map(v => (
              <div
                key={v}
                className="flex items-center justify-between rounded-md border px-3 py-2 text-sm"
              >
                <span>{v}</span>
                {canRemove && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-11 w-11 sm:h-7 sm:w-7 shrink-0"
                    title={t('common.delete')}
                    aria-label={t('common.delete')}
                    onClick={() => onRemove(v)}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function HoseOptions() {
  const t = useT();
  const hoseConfig = useApp(s => s.hoseConfig);
  const updateHoseConfig = useApp(s => s.updateHoseConfig);
  const toast = useApp(s => s.toast);
  const confirm = useConfirm();
  const can = useSectionRights()[SectionId.HOSE_OPTIONS];
  const rights = { canAdd: can.create, canRemove: can.delete };

  const addTo = (key: keyof typeof hoseConfig, v: string) => {
    updateHoseConfig({ [key]: [...(hoseConfig[key] ?? []), v] });
    toast(t('hoseOptions.addValue'));
  };
  // These lists drive the hose form's dropdowns, so a stray click silently
  // removed an option every existing hose still references (client #85).
  const removeFrom = async (key: keyof typeof hoseConfig, v: string) => {
    if (!(await confirm(`${t('common.delete')} ${v}?`))) return;
    updateHoseConfig({ [key]: (hoseConfig[key] ?? []).filter(x => x !== v) });
    toast(t('common.delete'));
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-4"
    >
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <Ruler className="h-6 w-6 text-primary" /> {t('hoseOptions.title')}
        </h1>
        <p className="text-sm text-muted-foreground">
          {t('hoseOptions.subtitle')}
        </p>
      </div>

      <div
        data-focus="hose-options-page"
        className="grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-3"
      >
        <OptionList
          titleKey="hoseOptions.hoseDiameters"
          items={sortOptions(hoseConfig.hoseDiameters)}
          placeholder={t('hoseOptions.placeholder')}
          onAdd={v => addTo('hoseDiameters', v)}
          onRemove={v => removeFrom('hoseDiameters', v)}
          {...rights}
        />
        <OptionList
          titleKey="hoseOptions.connectionDiameters"
          items={sortOptions(hoseConfig.connectionDiameters)}
          placeholder={t('hoseOptions.placeholder')}
          onAdd={v => addTo('connectionDiameters', v)}
          onRemove={v => removeFrom('connectionDiameters', v)}
          {...rights}
        />
        <OptionList
          titleKey="hoseOptions.hoseModels"
          items={sortOptions(hoseConfig.hoseModels)}
          placeholder={t('hoseOptions.modelPlaceholder')}
          onAdd={v => addTo('hoseModels', v)}
          onRemove={v => removeFrom('hoseModels', v)}
          {...rights}
        />
        <OptionList
          titleKey="hoseOptions.connections"
          items={sortOptions(hoseConfig.connections ?? [])}
          placeholder={t('hoseOptions.connectionPlaceholder')}
          onAdd={v => addTo('connections', v)}
          onRemove={v => removeFrom('connections', v)}
          {...rights}
        />
      </div>
    </motion.div>
  );
}
